import {Component, ViewChild} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {ResultListApiModel} from '../../../../api/models/result-list.api.model';
import {ProjectRestService} from '../../../../api/services/project.rest.service';
import {CurrentUserViewModel} from '../../../../core/models/current-user.view-model';
import {PostTypeEnum} from '../../../../core/enums/post-type.enum';
import {EventApiModel} from '../../../../api/models/event.api.model';
import {ModalInterface} from '../../modal.interface';
import {ModalState} from '../../modal-state.enum';
import {ModalStateEvent} from '../../modal-state-event';
import {ProjectViewModel} from '../../../../features/user/garage/project.view-model';
import {SocialInputComponent} from '../../../social/social-input/social-input.component';
import {SocialInputSubmitInterface} from '../../../social/social-input/social-input-submit.interface';
import {PostViewModel} from '../../../timeline/post.view-model';
import {PostApiModel} from '../../../../api/models/post-api.model';
import {MediaObjectApiModel} from '../../../../api/models/media-object.api-model';
import {TimelineDataProviderService} from '../../../timeline/timeline.data-provider.service';
import {ProjectApiModel} from '../../../../api/models/project.api-model';
import {ShareCardApiModel} from '../../../../api/models/share-card.api-model';
import {VehicleRestService} from '../../../../api/services/vehicle.rest.service';
import {VehicleApiModel} from '../../../../api/models/vehicle.api-model';
import {VehicleViewModel} from '../../../../features/user/garage/vehicle.view-model';
import {QueryParamsApiModel} from '../../../../api/models/query-params-api.model';
import {FilterParameterApiModel} from '../../../../api/models/filter-parameter.api.model';
import {map, mergeMap} from 'rxjs/operators';

@Component({
  selector: 'app-post-composer-modal',
  templateUrl: './post-composer-modal.component.html',
  styleUrls: ['./post-composer-modal.component.scss'],
  providers: [ProjectRestService],
})
export class PostComposerModalComponent implements ModalInterface {
  public data: any;
  public subject: Subject<ModalStateEvent>;
  public state: Observable<ModalStateEvent>;
  public currentUserVM: CurrentUserViewModel | null = null;
  public projectVM: ProjectViewModel | null = null;
  public eventAM: EventApiModel | null = null;
  public sharedPost: PostViewModel | null = null;
  public editingPost: PostViewModel | null = null;
  public initialMediaObjectAMs: MediaObjectApiModel[] = [];
  public selectableProjects: ProjectViewModel[] = [];
  public selectedProject: ProjectViewModel | null = null;
  public loadingProjects = false;
  public submitting = false;

  @ViewChild('composerInput') public composerInput?: SocialInputComponent;

  constructor(
    private readonly timelineDPS: TimelineDataProviderService,
    private readonly projectRS: ProjectRestService,
    private readonly vehicleRS: VehicleRestService,
  ) {
  }

  ngOnInit(): void {
    this.currentUserVM = this.data?.currentUserVM || null;
    this.editingPost = this.data?.editingPost || null;
    this.initialMediaObjectAMs = [...(this.editingPost?.media || [])];
    this.projectVM = this.editingPost?.project || this.data?.projectVM || null;
    this.eventAM = this.editingPost?.event || this.data?.eventAM || null;
    this.sharedPost = this.resolveSharedPost(this.data?.sharedPost || null);
    this.selectedProject = this.projectVM;

    if (this.canSwitchProjectTarget()) {
      this.loadProjects();
    }
  }

  public canSwitchProjectTarget(): boolean {
    return !this.editingPost && !this.projectVM && !this.eventAM && !!this.currentUserVM?.['@id'];
  }

  public isProjectLocked(): boolean {
    return !!this.projectVM && !this.eventAM;
  }

  public isEventLocked(): boolean {
    return !!this.eventAM;
  }

  public isGlobalEdit(): boolean {
    return !!this.editingPost && !this.projectVM && !this.eventAM;
  }

  public submitComposer(): void {
    if (this.submitting) {
      return;
    }

    this.composerInput?.onInputSubmitClick();
  }

  public get previewCard(): ShareCardApiModel | null {
    if (this.editingPost) {
      return this.editingPost.shareCard || null;
    }

    if (!this.sharedPost) {
      return null;
    }

    return {
      type: 'post',
      available: true,
      post: this.sharedPost.apiModel || this.sharedPost,
    } as ShareCardApiModel;
  }

  public onComposerSubmit(event: SocialInputSubmitInterface): void {
    if (this.submitting) {
      return;
    }

    this.submitting = true;
    if (this.editingPost) {
      const draft = Object.assign(new PostViewModel(), this.editingPost);
      draft.apiModel = Object.assign(new PostApiModel(), this.editingPost.apiModel);
      PostViewModel.update(event.content || '', [...(event.mediaObjectAMs || [])], draft);

      this.timelineDPS.updatePost(draft).subscribe({
        next: (updatedPostVM: PostViewModel) => {
          this.submitting = false;
          this.subject.next(new ModalStateEvent(ModalState.SUCCESS, updatedPostVM));
          event.input?.reset();
        },
        error: () => {
          this.submitting = false;
          event.input?.restoreAfterFailedSubmit();
        }
      });
      return;
    }

    const targetProject = this.isProjectLocked() ? this.projectVM : (this.selectedProject || null);
    const postVM = PostViewModel.create(
      event.content || '',
      this.resolvePostType(targetProject),
      event.mediaObjectAMs || [],
      targetProject,
      this.eventAM,
    );

    if (this.sharedPost?.id) {
      postVM.shareType = 'post';
      postVM.shareId = String(this.sharedPost.id);
      postVM.shareRef = {
        type: 'post',
        id: String(this.sharedPost.id),
      };
      postVM.shareCard = this.previewCard;
    }

    this.timelineDPS.createPost(postVM).subscribe({
      next: (createdPostVM: PostViewModel) => {
        this.submitting = false;
        this.subject.next(new ModalStateEvent(ModalState.SUCCESS, createdPostVM));
        event.input.reset();
      },
      error: () => {
        this.submitting = false;
        event.input?.restoreAfterFailedSubmit();
      }
    });
  }

  public projectLabel(project: ProjectViewModel | null): string {
    if (!project) {
      return '';
    }

    return project.displayVehicleIdentity
      || project['@id']
      || '';
  }

  private loadProjects(): void {
    if (!this.currentUserVM?.['@id']) {
      return;
    }

    this.loadingProjects = true;
    this.projectRS.getUserSubResourceCollection(this.currentUserVM['@id']).pipe(
      mergeMap((result: ResultListApiModel<ProjectApiModel>) => {
        return this.hydrateProjects(result.records || []);
      })
    ).subscribe({
      next: (projects: ProjectViewModel[]) => {
        this.selectableProjects = projects;
      },
      complete: () => {
        this.loadingProjects = false;
      }
    });
  }

  private hydrateProjects(projects: ProjectApiModel[]): Observable<ProjectViewModel[]> {
    if (!projects.length) {
      return of([]);
    }

    const vehicleRefs = projects
      .map((projectAM) => projectAM.vehicle)
      .filter((vehicleRef): vehicleRef is string => typeof vehicleRef === 'string' && !!vehicleRef);

    if (!vehicleRefs.length) {
      return of(projects.map((projectAM) => new ProjectViewModel(projectAM)));
    }

    const params = new QueryParamsApiModel();
    params.pagination = false;
    params.filters = vehicleRefs.map((vehicleRef) => new FilterParameterApiModel('id', vehicleRef));

    return this.vehicleRS.getVehicleCollection(params).pipe(
      map((result: ResultListApiModel<VehicleApiModel>) => {
        const vehicles = (result.records || []).map((vehicleAM) => new VehicleViewModel(vehicleAM));
        return projects.map((projectAM) => {
          const projectVM = new ProjectViewModel(projectAM);
          projectVM.vehicle = vehicles.find((vehicleVM) => vehicleVM['@id'] === projectAM.vehicle) || projectVM.vehicle;
          return projectVM;
        });
      })
    );
  }

  private resolvePostType(targetProject: ProjectViewModel | null): PostTypeEnum {
    if (this.eventAM) {
      return PostTypeEnum.EVENT_DISCUSSION_POST;
    }

    return targetProject ? PostTypeEnum.PROJECT_POST : PostTypeEnum.SIMPLE_POST;
  }

  private resolveSharedPost(post: PostViewModel | null): PostViewModel | null {
    return PostViewModel.ensureInstance(post);
  }
}
